import React, { FormEvent, useEffect, useState } from "react";
import {
	Button,
	Form,
	FormFeedback,
	FormGroup,
	Input,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Spinner,
} from "reactstrap";
import { getLoggedUserId } from "../../../services/selectors/Accounts";
import { showAlert } from "../../common/functions";
import uploadFileToBlob from "../../../services/Azure/azureBlob";
import { MusicPlatformAction } from "../../../actions/musicPlatformAction";
import { Dispatch } from "redux";

class Guid {
	static newGuid() {
		return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
			/[xy]/g,
			function (c) {
				const r = (Math.random() * 16) | 0,
					v = c === "x" ? r : (r & 0x3) | 0x8;
				return v.toString(16);
			}
		);
	}
}

interface MusicPlatformModalProps {
	isOpen: boolean;
	toggleDetailModal: () => void;
	id: number;
	dispatch: Dispatch<any>;
	musicPlatformData: any;
	detailLoading: any;
}

const MusicPlatformModal: React.FC<MusicPlatformModalProps> = ({
	isOpen = false,
	toggleDetailModal,
	id,
	dispatch,
	musicPlatformData,
	detailLoading,
}) => {
	const [platformName, setPlatformName] = useState("");
	const [logoFile, setLogoFile] = useState<any>(null);
	const [uploadedURL, setUploadedURL] = useState<string | null>(null);
	const [isEditLogoChange, setEditLogoChange] = useState<boolean>(false);
	const [isLoading, setLoading] = useState<boolean>(false);
	const [selectedLogoName, setSelectedLogoName] = useState("");
	const [isDetailLoaded, setDetailLoaded] = useState(false);
	const [errors, setErrors] = useState<{
		platformName?: string;
		logoFile?: string;
	}>({});

	const clearForm = () => {
		setDetailLoaded(false);
		setPlatformName("");
		setLogoFile(null);
		setUploadedURL(null);
		setSelectedLogoName("");
		setEditLogoChange(false);
		setErrors({});
		setLoading(false);
	};

	useEffect(() => {
		setLoading(false);
	}, [detailLoading]);

	useEffect(() => {
		if (isOpen && id > 0) {
			clearForm();
			dispatch(MusicPlatformAction.musicPlatformGetDetail(id));
		}
		if (isOpen && id === 0) {
			clearForm();
		}
	}, [id, isOpen, dispatch]);

	useEffect(() => {
		if (musicPlatformData.isSuccessSave === true) {
			clearForm();
			toggleDetailModal();
			setLoading(false);
			// musicPlatformData.isSuccessSave = false;
		}
	}, [musicPlatformData.isSuccessSave]);

	useEffect(() => {
		if (!isOpen || id <= 0) return;
		if (!musicPlatformData?.isDetailComplete) return;
		const detail = musicPlatformData?.musicPlatformsInput;
		if (!detail) return;
		if (detail.platformId && Number(detail.platformId) !== Number(id)) {
			return;
		}
		if (!isDetailLoaded) {
			const fileName = detail.platformLogoUrl?.split("/").pop() ?? "";
			setSelectedLogoName(fileName);
			setUploadedURL(detail.platformLogoUrl ?? null);
			setPlatformName(detail.platformName ?? "");
			setDetailLoaded(true);
		}
	}, [
		isOpen,
		id,
		musicPlatformData?.isDetailComplete,
		musicPlatformData?.musicPlatformsInput,
	]);

	useEffect(() => {
		clearForm();
	}, [isOpen]);

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;

		const extension = file.name.split(".").pop()?.toLowerCase();
		if (extension === "png" || extension === "jpg" || extension === "jpeg") {
			handleFileUpload(file);
			setErrors((prev) => ({ ...prev, logoFile: undefined }));
		} else {
			(event.target as HTMLInputElement).value = "";
			showAlert(`Please select a valid image file (png and jpg only).`, true);
			setLogoFile(null);
			// setSelectedLogoName('');
		}
	};

	const handleFileUpload = async (file: File) => {
		setSelectedLogoName(file.name);
		setEditLogoChange(true);
		setLogoFile(file);
		setUploadedURL(null);
	};

	const validate = () => {
		const newErrors: { platformName?: string; logoFile?: string } = {};
		if (!platformName.trim()) {
			newErrors.platformName = "Platform name is required.";
		}
		if (!logoFile && !uploadedURL) {
			newErrors.logoFile = "Logo image is required.";
		} else if (logoFile && typeof logoFile !== "string") {
			if (!["image/jpeg", "image/png"].includes(logoFile.type)) {
				newErrors.logoFile = "Only JPG or PNG format is allowed.";
			}
		}
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		try {
			if (validate() && (logoFile || uploadedURL)) {
				setLoading(true);
				let finalLogoUrl = uploadedURL;
				if (isEditLogoChange && logoFile instanceof File) {
					finalLogoUrl = await uploadFileToBlob(
						logoFile,
						Guid.newGuid(),
						"Image"
					);
				}
				const body = {
					...(id !== 0 && { platformId: id }),
					platformName: platformName,
					platformBaseUrl: "",
					platformLogoUrl: finalLogoUrl,
					authorUserId: parseInt(getLoggedUserId()),
				};
				await dispatch(MusicPlatformAction.musicPlatformSave(id, body));
			}
		} catch (error) {
			console.error("Failed to save music platform:", error);
			showAlert("Failed to save platform. Please try again.", true);
		}
	};

	return (
		<Modal isOpen={isOpen} size="xl" centered>
			<ModalHeader>
				{id > 0 ? "Edit Music Platform" : "Create New Music Platform"}
			</ModalHeader>
			<Form onSubmit={handleSubmit}>
				{id > 0 && !isDetailLoaded ? (
					<div className="text-center my-2">
						<Spinner color="primary" />
					</div>
				) : (
					<>
						<ModalBody>
							<FormGroup>
								<Label for="platformName" data-testid="platformName" className="col-form-label">
									Platform Name
								</Label>
								<Input
									id="platformName"
									type="text"
									placeholder="Enter platform name"
									data-testid="platformName"
									value={platformName}
									onChange={(e) => {
										setPlatformName(e.target.value);
										setErrors((prev) => ({ ...prev, platformName: undefined }));
									}}
									invalid={!!errors.platformName}
									maxLength={30}
								/>
								{errors.platformName && (
									<FormFeedback>{errors.platformName}</FormFeedback>
								)}
								{platformName?.length >= 30 && (
									<span className="text-warning">
										Character limit met. Maximum 30 characters allowed
									</span>
								)}
							</FormGroup>

							<FormGroup data-testid="imageFileContainer">
								<Label for="logoFile" data-testid="platformCoverimg" className="col-form-label">
									Logo Image{" "}
									<small className="text-muted">(JPG or PNG format only)</small>
								</Label>
								<Input
									id="logoFile"
									type="file"
									accept="image/jpeg, image/png"
									onChange={handleFileChange}
									invalid={!!errors.logoFile}
								/>
								{errors.logoFile && (
									<FormFeedback>{errors.logoFile}</FormFeedback>
								)}
								{selectedLogoName ? (
									<div className="mt-2 text-muted">
										Selected: {selectedLogoName}
									</div>
								) : uploadedURL ? (
									<div className="mt-2 text-muted">
										Saved Logo: {uploadedURL}
									</div>
								) : null}
							</FormGroup>
						</ModalBody>

						<ModalFooter>
							<Button
								type="button"
								color="secondary"
								disabled={isLoading}
								onClick={() => {
									// Close modal and clear local state
									toggleDetailModal();
									clearForm();
								}}
							>
								Cancel
							</Button>
							<Button type="submit" color="primary" disabled={isLoading} data-testid="confirmButton">
								{isLoading && <Spinner size="sm" />} {id > 0 ? "Update" : "Add"}
							</Button>
						</ModalFooter>
					</>
				)}
			</Form>
		</Modal>
	);
};

export default MusicPlatformModal;
